import { useMemo } from "react";
import { UnifiedUser } from "@/types/unified-user";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { SortableHeader, DateTimeCell } from "@/components/ui/table-cells";
import { Monitor, ExternalLink } from "lucide-react";

interface UserDevicesTabProps {
  user: UnifiedUser;
}

export function UserDevicesTab({ user }: UserDevicesTabProps) {
  const navigate = useNavigate();

  const columns: ColumnDef<NonNullable<UnifiedUser['devices']>[0]>[] = useMemo(() => [
    {
      accessorKey: "name",
      header: ({ column }) => <SortableHeader column={column}>Device Name</SortableHeader>,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Monitor className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium">{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge variant={status === 'active' ? 'default' : 'secondary'}>
            {status}
          </Badge>
        );
      },
    },
    {
      accessorKey: "lastSeen",
      header: ({ column }) => <SortableHeader column={column}>Last Seen</SortableHeader>,
      cell: ({ row }) => <DateTimeCell date={row.original.lastSeen} format="relative" />,
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/devices/${row.original.id}`);
          }}
        >
          <ExternalLink className="w-4 h-4" />
        </Button>
      ),
      size: 80,
    },
  ], [navigate]);

  if (!user.devices || user.devices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Monitor className="w-12 h-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Devices</h3>
        <p className="text-muted-foreground max-w-sm">
          This user hasn't enrolled any devices yet.
        </p>
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={user.devices}
      enableSorting={true}
      enableColumnFilters={true}
      onRowClick={(device) => navigate(`/admin/devices/${device.id}`)}
    />
  );
}
